import { FormEvent, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, TwoFactorRequiredError } from '@/auth/AuthContext';
import { api } from '@/api/client';
import { ChangePasswordGate } from '@/components/ChangePasswordGate';

function oauthReturnPath(search: string) {
  const raw = new URLSearchParams(search).get('returnTo');
  if (!raw?.startsWith('/api/oauth2/authorize?')) return null;
  try {
    const target = new URL(raw, window.location.origin);
    return target.origin === window.location.origin && target.pathname === '/api/oauth2/authorize'
      ? target.pathname + target.search : null;
  } catch { return null; }
}

interface DemoAccount {
  login: string;
  password: string;
  fullName: string;
  description: string;
  badge: string;
  badgeColor: string;
}

const DEMO_ACCOUNTS: DemoAccount[] = [
  {
    login: 'dwolski', password: 'hydro2026',
    fullName: 'Dominik Wolski',
    description: 'Właściciel · Geolog kat. V · Admin',
    badge: 'OWNER+ADMIN', badgeColor: 'bg-amber-500/20 text-amber-200 border-amber-400/30',
  },
  {
    login: 'mkowalski', password: 'hydro2026',
    fullName: 'Marek Kowalski',
    description: 'Geolog uprawniony kat. IV',
    badge: 'GEOLOG', badgeColor: 'bg-water-500/20 text-water-200 border-water-400/30',
  },
  {
    login: 'anowak', password: 'hydro2026',
    fullName: 'Aleksandra Nowak',
    description: 'Asystent / Kameralista',
    badge: 'ASYSTENT', badgeColor: 'bg-purple-500/20 text-purple-200 border-purple-400/30',
  },
  {
    login: 'enowak', password: 'hydro2026',
    fullName: 'Ewa Nowak',
    description: 'Pracownik biurowy',
    badge: 'BIURO', badgeColor: 'bg-emerald-500/20 text-emerald-200 border-emerald-400/30',
  },
  {
    login: 'admin', password: 'admin2026',
    fullName: 'Administrator Systemu',
    description: 'Wyłącznie administracja',
    badge: 'ADMIN', badgeColor: 'bg-red-500/20 text-red-200 border-red-400/30',
  },
];

export function Login() {
  const { user, login, error, loading, passwordChangeRequired } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [quickLoginPending, setQuickLoginPending] = useState<string | null>(null);
  // Panel "Szybkie logowanie testowe" widoczny tylko gdy backend zwraca demoMode=true
  // (czyli gdy HYDROWOLSKI_DATALOADER_SEED_DEMO=true). W produkcji wyłączony.
  const [demoMode, setDemoMode] = useState(false);
  useEffect(() => {
    api.get<{ demoMode: boolean }>('/api/public/system-info')
      .then(r => setDemoMode(r.demoMode === true))
      .catch(() => setDemoMode(false));
  }, []);
  // Stan 2FA — gdy != null, pokazujemy ekran TOTP i odpalamy retry z code-em.
  const [twoFactor, setTwoFactor] = useState<{ invalidCode: boolean; message: string } | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const nav = useNavigate();
  const loc = useLocation();
  const oauthReturn = oauthReturnPath(loc.search);
  useEffect(() => {
    if (user && oauthReturn && !loading && !submitting && !quickLoginPending && !passwordChangeRequired) {
      window.location.replace(oauthReturn);
    }
  }, [user, oauthReturn, loading, submitting, quickLoginPending, passwordChangeRequired]);

  if (user && oauthReturn) return passwordChangeRequired ? <ChangePasswordGate />
    : <div className="min-h-screen grid place-items-center">Powrót do połączenia z Claude…</div>;
  if (user) return <Navigate to={loc.state?.from ?? '/dashboard'} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(username, password);
      if (!oauthReturn) nav(loc.state?.from ?? '/dashboard', { replace: true });
    } catch (e) {
      if (e instanceof TwoFactorRequiredError) {
        setTwoFactor({ invalidCode: e.invalidCode, message: e.message });
      }
      /* inne błędy widoczne przez context.error */
    }
    finally { setSubmitting(false); }
  }

  /** Drugi krok — user wpisał code TOTP; retry login z tymi samymi credsami + code. */
  async function submitTotp(e: FormEvent) {
    e.preventDefault();
    if (totpCode.length < 6) return;
    setSubmitting(true);
    try {
      await login(username, password, totpCode);
      if (!oauthReturn) nav(loc.state?.from ?? '/dashboard', { replace: true });
    } catch (e) {
      if (e instanceof TwoFactorRequiredError) {
        // Code był nieprawidłowy — zostajemy w trybie TOTP, pokazujemy błąd
        setTwoFactor({ invalidCode: e.invalidCode, message: e.message });
        setTotpCode('');
      } else {
        // Inny błąd (np. session timeout, hasło wygasło) — wracamy do ekranu login+password
        setTwoFactor(null);
      }
    } finally { setSubmitting(false); }
  }

  function cancelTotp() {
    setTwoFactor(null);
    setTotpCode('');
    setPassword('');
  }

  async function quickLogin(acc: DemoAccount) {
    setQuickLoginPending(acc.login);
    setUsername(acc.login);
    setPassword(acc.password);
    setTwoFactor(null); setTotpCode('');
    try {
      await login(acc.login, acc.password);
      if (!oauthReturn) nav(loc.state?.from ?? '/dashboard', { replace: true });
    } catch (e) {
      if (e instanceof TwoFactorRequiredError) {
        setTwoFactor({ invalidCode: e.invalidCode, message: e.message });
      }
      /* inne błędy widoczne przez context.error */
    }
    finally { setQuickLoginPending(null); }
  }

  return (
    <div className="min-h-screen grid place-items-center bg-[radial-gradient(ellipse_at_top,#1a2440_0%,#0b1220_60%)] p-6">
      <div className="grid grid-cols-1 lg:grid-cols-[400px_440px] gap-6 items-start">

        <div className="bg-white/5 border border-white/10 backdrop-blur p-10 rounded-2xl text-ink-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-water-500 to-water-700 grid place-items-center">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2s7 8 7 13a7 7 0 1 1-14 0c0-5 7-13 7-13z"/>
              </svg>
            </div>
            <div>
              <h1 className="text-white text-[18px]">Hydro-Wolski Portal</h1>
              <div className="text-ink-300 text-[13px]">System zarządzania dokumentacją · v3.0</div>
            </div>
          </div>
          {error && !twoFactor && <div className="text-red-300 text-[12px] mb-3 p-2 rounded bg-red-500/10 border border-red-500/30">{error}</div>}

          {!twoFactor && (
            <form onSubmit={submit} className="flex flex-col gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-[12px] text-ink-300 font-semibold">Login</span>
                <input
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  autoFocus
                  required
                  className="bg-white/5 border border-white/10 px-3 py-2.5 rounded-md text-white text-[14px] outline-none focus:border-water-500"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[12px] text-ink-300 font-semibold">Hasło</span>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  className="bg-white/5 border border-white/10 px-3 py-2.5 rounded-md text-white text-[14px] outline-none focus:border-water-500"
                />
              </label>
              <button
                type="submit"
                disabled={submitting || !!quickLoginPending}
                className="btn btn-water w-full justify-center py-2.5 mt-2 disabled:opacity-60"
              >
                {submitting ? 'Logowanie…' : 'Zaloguj się'}
              </button>
            </form>
          )}

          {twoFactor && (
            <form onSubmit={submitTotp} className="flex flex-col gap-3">
              <div className="text-center mb-2">
                <div className="w-12 h-12 rounded-full bg-water-500/20 border border-water-500/40 mx-auto mb-2 grid place-items-center">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
                       className="text-water-300">
                    <path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6z"/>
                    <path d="M9 12l2 2 4-4"/>
                  </svg>
                </div>
                <div className="text-white text-[14px] font-semibold">Weryfikacja dwustopniowa</div>
                <div className="text-ink-300 text-[12px]">
                  Otwórz aplikację 2FA i wpisz 6-cyfrowy kod dla <span className="font-mono text-water-300">{username}</span>
                </div>
              </div>
              {twoFactor.invalidCode && (
                <div className="text-red-300 text-[12px] p-2 rounded bg-red-500/10 border border-red-500/30">
                  {twoFactor.message}
                </div>
              )}
              <label className="flex flex-col gap-1">
                <span className="text-[12px] text-ink-300 font-semibold">Kod TOTP (lub kod zapasowy w formacie 1234-5678)</span>
                <input
                  value={totpCode}
                  onChange={e => setTotpCode(e.target.value.replace(/[^0-9-]/g, ''))}
                  autoFocus
                  required
                  inputMode="numeric"
                  maxLength={9}
                  placeholder="000000"
                  className="bg-white/5 border border-white/10 px-3 py-2.5 rounded-md text-white text-[18px]
                             font-mono text-center tracking-[0.3em] outline-none focus:border-water-500"
                />
              </label>
              <button
                type="submit"
                disabled={submitting || totpCode.length < 6}
                className="btn btn-water w-full justify-center py-2.5 mt-1 disabled:opacity-60"
              >
                {submitting ? 'Weryfikuję…' : 'Potwierdź kod'}
              </button>
              <button
                type="button"
                onClick={cancelTotp}
                disabled={submitting}
                className="text-[11px] text-ink-400 hover:text-ink-200 disabled:opacity-40"
              >
                ← Wróć do logowania
              </button>
            </form>
          )}
        </div>

        {demoMode && (
        <div className="bg-white/5 border border-white/10 backdrop-blur p-6 rounded-2xl text-ink-100">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-white text-[14px] font-semibold">Szybkie logowanie testowe</div>
              <div className="text-ink-400 text-[11px]">Kliknij konto, aby się zalogować</div>
            </div>
            <span className="text-[10px] text-ink-400 uppercase tracking-wider px-2 py-1 rounded bg-amber-500/10 border border-amber-500/30 text-amber-200">DEMO</span>
          </div>

          <div className="flex flex-col gap-2">
            {DEMO_ACCOUNTS.map(acc => (
              <button
                key={acc.login}
                type="button"
                onClick={() => quickLogin(acc)}
                disabled={submitting || !!quickLoginPending}
                className="text-left p-3 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 hover:border-water-500/40 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-start gap-3"
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-water-500/30 to-water-700/30 border border-water-500/30 grid place-items-center text-water-200 font-semibold text-[12px] shrink-0">
                  {acc.fullName.split(' ').map(s => s[0]).join('').slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <div className="text-white text-[13px] font-semibold truncate">{acc.fullName}</div>
                    <span className={'text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ' + acc.badgeColor}>
                      {acc.badge}
                    </span>
                  </div>
                  <div className="text-ink-300 text-[11px] truncate">{acc.description}</div>
                  <div className="text-ink-400 text-[10px] font-mono mt-0.5">{acc.login} · {acc.password}</div>
                </div>
                {quickLoginPending === acc.login && (
                  <div className="self-center text-water-400 text-[11px]">…</div>
                )}
              </button>
            ))}
          </div>

          <div className="mt-4 text-[10px] text-ink-500 leading-relaxed border-t border-white/5 pt-3">
            Konta utworzone w środowisku demonstracyjnym (<code className="text-water-300">HYDROWOLSKI_DATALOADER_SEED_DEMO=true</code>).
            Nie używać w produkcji — wyłącz seed-demo i utwórz prawdziwe konto przez bootstrap-admin.
          </div>
        </div>
        )}

      </div>
    </div>
  );
}
